/**
 * AI表格字段名 → 面试登记表字段 映射配置
 * 请根据你的 AI 表格实际列名修改此文件
 */
export const FIELD_MAP = {
  // 基本信息
  position: '应聘岗位',
  availableDate: '可到岗时间',
  expectedSalary: '期望薪资',
  name: '姓名',
  inviteHr: '邀约HR',
  ethnicity: '民族',
  phone: '联系电话',
  birthDate: '出生日期',
  weight: '体重',
  height: '身高',
  healthStatus: '健康状况',
  nativePlace: '籍贯',
  maritalStatus: '婚姻状况',
  email: '邮箱',
  hukouAddress: '户籍地址',
  currentAddress: '现居住址',

  // 紧急联系人
  emergencyContact: '紧急联络人',
  emergencyRelation: '紧急联系人关系',
  emergencyPhone: '紧急联络人联系电话',

  // 职业状态
  employmentStatus: '职业状态',
  travelWillingness: '出差意愿调查',

  // 公司关系
  hasRelativeInCompany: '是否有亲戚、朋友、熟人在本公司任职?',
  relativeName: '亲友姓名',

  // 劳动关系
  laborRelation: '和原单位劳动关系',
  confidentialityAgreement: '是否签订过保密和竞业协议',

  // 身体状况
  pregnancyPlan: '目前是否有身孕，或近一年是否有怀孕计划（适用于女性应聘者）？',
  familyMedicalHistory: '是否有家庭遗传病史或重大病史',

  // 签字
  signature: '应聘人签字',
  interviewDate: '面试日期',

  // 背面 - 个人能力/特质
  personalityAdjectives: '最能描述您性格的三个形容词',
  interests: '您的兴趣及特长',

  // 背面 - 面试评价（多为 HR 填写）
  firstInterviewComment: '初试人员评价',
  firstInterviewSign: '初试签名',
  firstInterviewDate: '初试日期',
  secondInterviewComment: '复试人员评价',
  secondInterviewSign: '复试签名',
  secondInterviewDate: '复试日期',
  hiringOpinion: '录用意见',
} as const;

/** 价值观评估表结构（背面） */
export const VALUES_ASSESSMENT_GROUPS = [
  {
    value: '客户第一',
    fieldName: '客户第一',
    standards: [
      '客户导向',
      '高质量产品：安全、稳定、创新',
      '高质量服务：专业素养、快速响应、可靠奉献',
      '客户保密',
    ],
  },
  {
    value: '积极主动',
    fieldName: '积极主动',
    standards: [
      '乐观向上：自我激励、永不言弃、向上同登',
      '勇争第一：目标明确、全力以赴、追求卓越',
      '创新进取：开放思维、积极思考、不断突破',
      '拥抱变化：开阔心态、寻找机遇、迎接挑战',
    ],
  },
  {
    value: '团队合作',
    fieldName: '团队合作',
    standards: [
      '目标一致：目标明确、团结协同、结果导向',
      '信任尊重：平等相待、坦诚沟通、相互支持',
      '沟通协作：主动沟通、善思广益、积极配合',
      '责任共担：积极参与、贡献力量、荣辱与共',
    ],
  },
  {
    value: '承担成长',
    fieldName: '承担成长',
    standards: [
      '勇于挑战：接受使命、迎难而上、超越自我',
      '勇于担当：敢于面对、勇于解决、履职尽责',
      '持续学习：主动学习、学以致用、反思总结',
      '追求卓越：登高而立、精益求精、超越期望',
    ],
  },
] as const;

/** 表格选项与纸质表措辞差异时的兼容别名 */
const VALUE_STANDARD_ALIASES: Record<string, string[]> = {
  '高质量产品：安全、稳定、创新': ['高质量产品：安全、稳定、创新'],
  '高质量服务：专业素养、快速响应、可靠奉献': ['高质量服务：专业素养、快速响应、可靠售后'],
  '乐观向上：自我激励、永不言弃、向上同登': ['乐观向上：自我激励、永不言弃、向上向善'],
  '拥抱变化：开阔心态、寻找机遇、迎接挑战': ['拥抱变化：开放心态、寻找机遇、迎接挑战'],
  '目标一致：目标明确、团结协同、结果导向': ['目标一致：目标制定、目标协同、结果导向'],
  '信任尊重：平等相待、坦诚沟通、相互支持': ['信任尊重：平等对待、坦诚沟通、相互支持'],
  '沟通协作：主动沟通、善思广益、积极配合': ['沟通协作：主动沟通、集思广益、积极配合'],
  '责任共担：积极参与、贡献力量、荣辱与共': ['责任共担：明确职责、群策群力、荣辱与共'],
  '勇于挑战：接受使命、迎难而上、超越自我': ['勇于挑战：坚定信念、迎难而上、越挫越勇'],
  '勇于担当：敢于面对、勇于解决、履职尽责': ['勇于担当：积极应对、敢于决策、履职尽责'],
  '追求卓越：登高而立、精益求精、超越期望': ['追求卓越：择高而立、精益求精、超越期望'],
};

/** 未来发展规划（背面） */
export const DEVELOPMENT_PLAN_ROWS = [
  {
    key: 'short',
    label: '短期（2年内）',
    lifeField: '短期生活目标',
    careerField: '短期事业目标',
    lifeKeywords: ['短期生活', '生活目标'],
    careerKeywords: ['短期事业', '事业目标'],
  },
  {
    key: 'medium',
    label: '中期（3-5年）',
    lifeField: '中期生活目标',
    careerField: '中期事业目标',
    lifeKeywords: ['中期生活', '中期（3-5年）', '3-5年'],
    careerKeywords: ['中期事业', '中期（3-5年）', '3-5年', '规划'],
  },
  {
    key: 'long',
    label: '长期（5年以后）',
    lifeField: '长期生活目标',
    careerField: '长期事业目标',
    lifeKeywords: ['长期生活'],
    careerKeywords: ['长期事业'],
  },
] as const;

/** 家庭信息（最多3行）— 兼容「家庭信息xxx」与「家庭xxx1」两种列名 */
export const FAMILY_FIELDS = [
  {
    name: '家庭信息姓名', relation: '家庭信息关系', age: '家庭信息年龄',
    employer: '家庭信息工作单位', position: '家庭信息岗位', address: '家庭信息家庭住址',
  },
  {
    name: '家庭姓名2', relation: '家庭关系2', age: '家庭年龄2',
    employer: '家庭工作单位2', position: '家庭岗位2', address: '家庭住址2',
  },
  {
    name: '家庭姓名3', relation: '家庭关系3', age: '家庭年龄3',
    employer: '家庭工作单位3', position: '家庭岗位3', address: '家庭住址3',
  },
];

/** 工作经历（最多3行）— 支持起止时间分列 */
export const WORK_FIELDS = [
  {
    periodStart: '任职时间（起）', periodEnd: '任职时间（止）',
    company: '公司名称', position: '职位', salary: '薪资',
    reason: '离职原因', reference: '证明人及电话',
  },
  {
    period: '工作任职时间2', company: '工作公司名称2', position: '工作职位2',
    salary: '工作薪资2', reason: '工作离职原因2', reference: '工作证明人2',
  },
  {
    period: '工作任职时间3', company: '工作公司名称3', position: '工作职位3',
    salary: '工作薪资3', reason: '工作离职原因3', reference: '工作证明人3',
  },
];

/** 教育经历（最多3行）— 支持起止时间分列 */
export const EDUCATION_FIELDS = [
  {
    periodStart: '学习时间（起）', periodEnd: '学习时间（止）',
    school: '学校名称', major: '专业', degree: '学历',
  },
  {
    period: '教育学习时间2', school: '教育学校名称2', major: '教育专业2', degree: '教育学历2',
  },
  {
    period: '教育学习时间3', school: '教育学校名称3', major: '教育专业3', degree: '教育学历3',
  },
];

export type FormData = Record<string, string>;

/** 字段别名：主字段名匹配不到时，依次尝试备选列名 */
export const FIELD_ALIASES: Partial<Record<keyof typeof FIELD_MAP, string[]>> = {
  availableDate: ['到岗时间', '可到岗日期'],
  position: ['面试岗位', '岗位'],
  phone: ['电话', '手机号', '联系方式'],
  inviteHr: ['邀约 HR', '邀约hr', 'HR邀约'],
  birthDate: ['生日', '出生年月'],
  email: ['电子邮箱', 'E-mail'],
  nativePlace: ['家乡'],
  hukouAddress: ['户口地址', '户口所在地', '户籍所在地'],
  emergencyContact: ['紧急联系人'],
  emergencyRelation: ['紧急联系人关系', '紧急联络人关系'],
  emergencyPhone: ['紧急联系人电话', '紧急联络人电话', '紧急联系人联系电话'],
  interviewDate: ['面试时间'],
  currentAddress: ['现居地址'],
  travelWillingness: ['出差意愿'],
  confidentialityAgreement: [
    '是否签订保密或竞业协议?',
    '是否签订保密或竞业协议',
    '是否签订保密或竞业协议？',
    '是否签订过保密或竞业协议',
    '保密或竞业协议',
  ],
  hasRelativeInCompany: [
    '是否有亲戚、朋友、熟人在本公司任职？',
    '是否有亲戚、朋友、熟人在本公司任职',
    '是否亲戚朋友在本公司任职',
    '是否有亲友在本公司任职',
    '是否有亲友在公司任职',
  ],
  laborRelation: ['和原单位劳动关系', '与原单位劳动关系'],
  pregnancyPlan: [
    '是否有身孕或怀孕计划',
    '目前是否有身孕, 或近一年是否有怀孕计划 (适用于女性应聘者)?',
    '目前是否有身孕，或近一年是否有怀孕计划（适用于女性应聘者）',
    '目前是否有身孕或近一年是否有怀孕计划',
  ],
  familyMedicalHistory: [
    '是否有家庭遗传病史或重大病史?',
    '是否有家庭遗传病史或重大病史？',
  ],
  personalityAdjectives: ['性格的三个形容词', '性格形容词'],
  interests: ['兴趣及特长', '兴趣爱好及特长'],
  firstInterviewComment: ['初试评价', '初试人员评语'],
  secondInterviewComment: ['复试评价', '复试人员评语'],
  hiringOpinion: ['录用意见', '是否录用'],
};

/** 字段名模糊匹配关键词（列名不完全一致时兜底） */
const FIELD_KEYWORDS: Partial<Record<keyof typeof FIELD_MAP, string[]>> = {
  travelWillingness: ['出差意愿'],
  hasRelativeInCompany: ['亲戚', '本公司任职'],
  confidentialityAgreement: ['保密', '竞业'],
  currentAddress: ['现居'],
  hukouAddress: ['户籍', '户口'],
  pregnancyPlan: ['身孕', '怀孕计划'],
  familyMedicalHistory: ['遗传病史', '重大病史'],
  personalityAdjectives: ['性格', '形容词'],
  interests: ['兴趣', '特长'],
  firstInterviewComment: ['初试'],
  secondInterviewComment: ['复试'],
  hiringOpinion: ['录用'],
};

/** 复选框选项别名：表格里的选项文字可能与表单不完全一致 */
const OPTION_ALIASES: Record<string, string[]> = {
  可接受: ['可接受', '接受', '愿意', '可以', '能接受'],
  不能接受: ['不能接受', '不接受', '不愿意', '不可接受', '不能'],
  是: ['是', '有', '已签订', '已签', '签订', '已签署'],
  否: ['否', '无', '未签订', '未签', '没有', '未签署'],
  在职: ['在职', '在岗', '就业中'],
  待业: ['待业', '失业', '无业'],
  兼职: ['兼职', '自由职业'],
  已解除: ['已解除', '已终止', '解除'],
  未解除: ['未解除', '未终止', '存续'],
  予以录用: ['予以录用', '录用', '同意录用'],
  有待进一步考虑: ['有待进一步考虑', '进一步考虑', '待定'],
  不予考虑: ['不予考虑', '不录用', '拒绝'],
};

/** 需要格式化为日期的字段 */
const DATE_FIELDS = new Set<keyof typeof FIELD_MAP>([
  'availableDate', 'birthDate', 'interviewDate',
  'firstInterviewDate', 'secondInterviewDate',
]);

/** 工作经历/教育经历中的日期字段 */
const PERIOD_FIELD_KEYS = ['period'];

/** 带单位的数值字段 */
const UNIT_FIELDS: Partial<Record<keyof typeof FIELD_MAP, string>> = {
  weight: 'Kg',
  height: 'Cm',
};

/** 判断是否为地址/级联类数组 */
function isLocationArray(parts: string[]): boolean {
  return parts.length > 0 && parts.every((p) => /省|市|区|县|自治区|特别行政区/.test(p) || p.length <= 20);
}

/** 提取嵌套对象中的名称 */
function unwrapRegionItem(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  if (typeof value === 'object') {
    const item = value as Record<string, unknown>;
    if (item.name) return String(item.name);
    if (item.label) return String(item.label);
    if (item.text) return String(item.text);
  }
  return extractCellValue(value);
}

/** 深度收集对象中的地址片段 */
function collectDeepRegionParts(value: unknown, depth = 0): string[] {
  if (depth > 6 || value === null || value === undefined) return [];

  if (typeof value === 'string' || typeof value === 'number') {
    const text = String(value).trim();
    return text ? [text] : [];
  }

  if (Array.isArray(value)) {
    return value.flatMap((item) => collectDeepRegionParts(item, depth + 1));
  }

  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    for (const key of ['fullAddress', 'fullPath', 'address', 'displayText', 'text', 'name', 'label']) {
      if (obj[key] !== undefined) {
        const parts = collectDeepRegionParts(obj[key], depth + 1);
        if (parts.length) return parts;
      }
    }
    return extractRegionParts(obj);
  }

  return [];
}

/** 从行政区域对象中提取省市区片段 */
function extractRegionParts(obj: Record<string, unknown>): string[] {
  const orderedKeys = [
    'province', 'city', 'district', 'county', 'detail',
    'provinceName', 'cityName', 'districtName', 'areaName',
    '省', '市', '区', '县', '路',
  ];
  const ordered = orderedKeys
    .map((key) => unwrapRegionItem(obj[key]))
    .filter(Boolean);
  if (ordered.length) return ordered;

  if (Array.isArray(obj.levels)) {
    return obj.levels.map((v) => extractCellValue(v)).filter(Boolean);
  }
  if (Array.isArray(obj.value)) {
    return obj.value.map((v) => extractCellValue(v)).filter(Boolean);
  }
  if (Array.isArray(obj.items)) {
    return obj.items.map((v) => extractCellValue(v)).filter(Boolean);
  }
  if (Array.isArray(obj.regions)) {
    return obj.regions.map((v) => extractCellValue(v)).filter(Boolean);
  }

  const numericKeys = Object.keys(obj)
    .filter((k) => /^\d+$/.test(k))
    .sort((a, b) => Number(a) - Number(b));
  if (numericKeys.length) {
    return numericKeys.map((k) => extractCellValue(obj[k])).filter(Boolean);
  }

  const regionValues = Object.values(obj)
    .map((v) => extractCellValue(v))
    .filter((v) => v && /省|市|区|县|自治区|特别行政区/.test(v));
  if (regionValues.length) return regionValues;

  return [];
}

/** 解包单元格嵌套结构 */
function unwrapLocationPayload(value: unknown): unknown {
  if (value === null || value === undefined) return value;
  if (typeof value !== 'object' || Array.isArray(value)) return value;

  const obj = value as Record<string, unknown>;
  if (obj.data !== undefined) return unwrapLocationPayload(obj.data);
  if (obj.result !== undefined) return unwrapLocationPayload(obj.result);
  if (obj.cellValue !== undefined) return unwrapLocationPayload(obj.cellValue);
  if (obj.value !== undefined && typeof obj.value === 'object') {
    return unwrapLocationPayload(obj.value);
  }
  return value;
}

/** 格式化地址/级联/行政区域字段值 */
export function formatLocationValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '';

  const unwrapped = unwrapLocationPayload(value);

  if (typeof unwrapped === 'string') {
    const text = unwrapped.trim();
    if (!text || ['未填', '无', '-', '—', '暂无'].includes(text)) return '';
    return text.replace(/\//g, '');
  }

  if (Array.isArray(unwrapped)) {
    const parts = unwrapped.map((v) => extractCellValue(v)).filter(Boolean);
    if (!parts.length) return '';
    return isLocationArray(parts) ? parts.join('') : parts.join(', ');
  }

  if (typeof unwrapped === 'object') {
    const obj = unwrapped as Record<string, unknown>;
    if (obj.fullAddress) return String(obj.fullAddress).replace(/\//g, '');
    if (obj.fullPath) return String(obj.fullPath).replace(/\//g, '');
    if (obj.address) return String(obj.address).replace(/\//g, '');
    if (obj.displayText) return String(obj.displayText).replace(/\//g, '');

    const regionParts = collectDeepRegionParts(obj);
    if (regionParts.length) {
      return isLocationArray(regionParts) ? regionParts.join('') : regionParts.join(', ');
    }

    if (obj.name !== undefined && /省|市|区|县/.test(String(obj.name))) {
      return String(obj.name).replace(/\//g, '');
    }
    if (obj.text !== undefined) return String(obj.text).replace(/\//g, '');
    if (obj.label !== undefined) return String(obj.label).replace(/\//g, '');
  }

  return extractCellValue(unwrapped);
}

/** 从 DingTalk 单元格值中提取原始文本 */
export function extractCellValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (Array.isArray(value)) {
    const parts = value.map((v) => extractCellValue(v)).filter(Boolean);
    if (!parts.length) return '';
    return isLocationArray(parts) ? parts.join('') : parts.join(', ');
  }
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    // 链接字段 { text, link }
    if (obj.link !== undefined) {
      const link = String(obj.link);
      const text = obj.text ? String(obj.text) : '';
      return text || link;
    }
    // 地址/级联字段对象
    if (obj.fullAddress || obj.fullPath || obj.address) {
      return formatLocationValue(value);
    }
    const regionParts = extractRegionParts(obj);
    if (regionParts.length) return regionParts.join('');
    if (obj.name !== undefined) return String(obj.name);
    if (obj.text !== undefined) return String(obj.text);
    if (obj.label !== undefined) return String(obj.label);
    if (Array.isArray(obj.value)) return formatLocationValue(obj.value);
  }
  return String(value);
}

/**
 * 时间戳转毫秒：兼容秒(10位)与毫秒(13位)，以及 1970~2001 年的 11~12 位毫秒值
 * （旧逻辑 num < 1e12 一律乘 1000，会把 1997 年的毫秒戳误放大到 29797 年）
 */
function timestampToMilliseconds(num: number): number {
  if (num >= 1e12) return num;
  if (num >= 1e11) return num;
  if (num >= 1e9) return num * 1000;
  return num;
}

/** 毫秒时间戳 → YYYY-MM-DD */
export function formatDate(value: string): string {
  if (!value) return '';
  const trimmed = value.trim();

  // 已是常见日期格式，直接返回（去掉时间部分）
  if (/^\d{4}[-/年]\d{1,2}[-/月]\d{1,2}/.test(trimmed)) {
    return trimmed
      .replace(/年/g, '-').replace(/月/g, '-').replace(/日/g, '')
      .replace(/\//g, '-')
      .split(' ')[0];
  }

  // 毫秒或秒级时间戳
  const num = Number(trimmed);
  if (!isNaN(num) && num > 0) {
    const ms = timestampToMilliseconds(num);
    const date = new Date(ms);
    if (!isNaN(date.getTime())) {
      const y = date.getFullYear();
      if (y >= 1900 && y <= 2100) {
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
      }
    }
  }

  return trimmed;
}

/** 日期区间：支持 "起~止" 两个时间戳 */
export function formatDateRange(value: string): string {
  if (!value) return '';
  const trimmed = value.trim();

  if (trimmed.includes('~') || trimmed.includes('至')) {
    const parts = trimmed.split(/[~至]/);
    if (parts.length === 2) {
      return `${formatDate(parts[0].trim())} ~ ${formatDate(parts[1].trim())}`;
    }
  }

  // 纯数字时间戳
  if (/^\d+$/.test(trimmed)) {
    return formatDate(trimmed);
  }

  return trimmed;
}

/** 邮箱：去掉多余 @ 前缀，保留完整地址 */
export function formatEmail(value: string): string {
  if (!value) return '';
  let email = value.trim();
  // 去掉开头的 @ 符号（纸质表 @ 占位符残留）
  while (email.startsWith('@')) {
    email = email.slice(1);
  }
  return email;
}

/** 数值 + 单位：避免重复追加单位 */
export function formatWithUnit(value: string, unit: string): string {
  if (!value) return '';
  const trimmed = value.trim();
  if (new RegExp(unit, 'i').test(trimmed)) return trimmed;
  return `${trimmed} ${unit}`;
}

/** 薪资格式化 */
export function formatSalary(value: string): string {
  if (!value) return '';
  const num = Number(value.replace(/[,，]/g, ''));
  if (!isNaN(num) && /^\d+(\.\d+)?$/.test(value.replace(/[,，]/g, ''))) {
    return `${num.toLocaleString('zh-CN')} 元`;
  }
  return value;
}

/** 归一化行为准则文本（兼容中英文冒号） */
function normalizeStandardText(text: string): string {
  return text
    .replace(/[：:]/g, ':')
    .replace(/\s/g, '')
    .replace(/[?？]/g, '');
}

/** 从多选字段提取已选文本列表 */
export function extractMultiSelectTexts(value: unknown): string[] {
  if (value === null || value === undefined || value === '') return [];
  if (Array.isArray(value)) {
    return value.flatMap((item) => {
      if (typeof item === 'object' && item !== null && 'name' in item) {
        return [String((item as { name: string }).name)];
      }
      return [extractCellValue(item)];
    }).filter(Boolean);
  }
  if (typeof value === 'string') {
    return value.split(/[,，;；]/).map((s) => s.trim()).filter(Boolean);
  }
  const text = extractCellValue(value);
  return text ? [text] : [];
}

/** 判断价值观行为准则是否在自评多选中 */
export function isValueStandardSelected(
  fields: Record<string, unknown>,
  fieldName: string,
  standardText: string,
): boolean {
  const raw = fields[fieldName];
  if (raw === undefined || raw === null || raw === '') {
    const matchedKey = Object.keys(fields).find(
      (k) => normalizeFieldName(k) === normalizeFieldName(fieldName),
    );
    if (!matchedKey) return false;
    return isValueStandardSelected(fields, matchedKey, standardText);
  }

  const targets = [
    standardText,
    ...(VALUE_STANDARD_ALIASES[standardText] ?? []),
  ].map(normalizeStandardText);
  const selected = extractMultiSelectTexts(raw);
  return selected.some((item) => {
    const normalized = normalizeStandardText(item);
    return targets.some((target) => normalized === target
      || normalized.includes(target)
      || target.includes(normalized));
  });
}

/** 按关键词从记录中取值 */
function resolveFieldByKeywords(
  fields: Record<string, unknown>,
  keywords: string[],
): string {
  for (const [fieldName, fieldValue] of Object.entries(fields)) {
    if (keywords.some((kw) => fieldName.includes(kw))) {
      const text = extractCellValue(fieldValue);
      if (text) return text;
    }
  }
  return '';
}

/** 列名须同时包含给定片段（兼容「请描述您未来的发展规划：中期（3-5年）生活目标」等长列名） */
function resolveFieldByAllKeywords(
  fields: Record<string, unknown>,
  keywordGroups: string[][],
): string {
  for (const keywords of keywordGroups) {
    for (const [fieldName, fieldValue] of Object.entries(fields)) {
      if (keywords.every((kw) => fieldName.includes(kw))) {
        const text = extractCellValue(fieldValue);
        if (text) return text;
      }
    }
  }
  return '';
}

const DEVELOPMENT_PLAN_MATCH_GROUPS: Record<
  typeof DEVELOPMENT_PLAN_ROWS[number]['key'],
  { life: string[][]; career: string[][] }
> = {
  short: {
    life: [['短期', '生活'], ['2年内', '生活']],
    career: [['短期', '事业'], ['2年内', '事业']],
  },
  medium: {
    life: [
      ['中期', '生活'],
      ['3-5年', '生活'],
      ['3', '5年', '生活'],
      ['发展规划', '中期', '生活'],
    ],
    career: [
      ['中期', '事业'],
      ['3-5年', '事业'],
      ['3', '5年', '事业'],
      ['规划', '中期'],
      ['规划', '3-5年'],
      ['发展规划', '中期', '事业'],
    ],
  },
  long: {
    life: [['长期', '生活'], ['5年以后', '生活'], ['5年', '以后', '生活']],
    career: [['长期', '事业'], ['5年以后', '事业'], ['5年', '以后', '事业']],
  },
};

/** 解析发展规划某一格（生活/事业目标） */
function resolveDevelopmentPlanField(
  fields: Record<string, unknown>,
  row: typeof DEVELOPMENT_PLAN_ROWS[number],
  kind: 'life' | 'career',
): string {
  const primary = kind === 'life' ? row.lifeField : row.careerField;
  const direct = extractCellValue(fields[primary]);
  if (direct) return direct;

  const normalizedTarget = normalizeFieldName(primary);
  for (const [fieldName, fieldValue] of Object.entries(fields)) {
    if (normalizeFieldName(fieldName) === normalizedTarget) {
      const text = extractCellValue(fieldValue);
      if (text) return text;
    }
  }

  const groups = DEVELOPMENT_PLAN_MATCH_GROUPS[row.key][kind];
  const fromGroups = resolveFieldByAllKeywords(fields, groups);
  if (fromGroups) return fromGroups;

  const orKeywords = kind === 'life' ? row.lifeKeywords : row.careerKeywords;
  return resolveFieldByKeywords(fields, [...orKeywords]);
}

/** 从同名多行记录补全仍为空的发展规划字段 */
function fillMissingDevelopmentPlanFields(
  form: FormData,
  allRecords: Record<string, unknown>[],
) {
  DEVELOPMENT_PLAN_ROWS.forEach((row) => {
    (['life', 'career'] as const).forEach((kind) => {
      const key = `plan_${row.key}_${kind}` as keyof FormData;
      if (form[key]) return;
      for (const fields of allRecords) {
        const value = resolveDevelopmentPlanField(fields, row, kind);
        if (value) {
          form[key] = value;
          return;
        }
      }
    });
  });
}

/** 判断复选框选项是否匹配 */
export function isOptionMatch(value: string, option: string): boolean {
  if (!value) return false;
  const normalized = value.trim();
  const aliases = OPTION_ALIASES[option] ?? [option];
  return aliases.some(
    (alias) => normalized === alias || normalized.includes(alias),
  );
}

/** 归一化列名，消除标点/和或/过 等差异 */
function normalizeFieldName(name: string): string {
  return name
    .replace(/[?？。.\s、,，]/g, '')
    .replace(/和/g, '或')
    .replace(/过/g, '');
}

/** 按主字段名 + 别名 + 归一化匹配 + 模糊关键词从记录中取值 */
function resolveFieldValue(
  fields: Record<string, unknown>,
  key: keyof typeof FIELD_MAP,
): string {
  const tryExtract = (fieldName: string): string => {
    const val = fields[fieldName];
    if (val !== undefined && val !== null && val !== '') {
      return extractCellValue(val);
    }
    return '';
  };

  const primary = FIELD_MAP[key];
  const fromPrimary = tryExtract(primary);
  if (fromPrimary) return fromPrimary;

  for (const alias of FIELD_ALIASES[key] ?? []) {
    const val = tryExtract(alias);
    if (val) return val;
  }

  const namesToTry = [primary, ...(FIELD_ALIASES[key] ?? [])];
  for (const name of namesToTry) {
    const normalizedTarget = normalizeFieldName(name);
    for (const [fieldName, fieldValue] of Object.entries(fields)) {
      if (
        normalizeFieldName(fieldName) === normalizedTarget
        && fieldValue !== undefined
        && fieldValue !== null
        && fieldValue !== ''
      ) {
        return extractCellValue(fieldValue);
      }
    }
  }

  const keywords = FIELD_KEYWORDS[key] ?? [];
  if (keywords.length > 1) {
    for (const [fieldName, fieldValue] of Object.entries(fields)) {
      if (
        keywords.every((keyword) => fieldName.includes(keyword))
        && fieldValue !== undefined
        && fieldValue !== null
        && fieldValue !== ''
      ) {
        return extractCellValue(fieldValue);
      }
    }
  }

  for (const keyword of keywords) {
    for (const [fieldName, fieldValue] of Object.entries(fields)) {
      if (
        fieldName.includes(keyword)
        && fieldValue !== undefined
        && fieldValue !== null
        && fieldValue !== ''
      ) {
        return extractCellValue(fieldValue);
      }
    }
  }

  return '';
}

interface AddressParts {
  province: string;
  city: string;
  district: string;
  detail: string;
}

/** 解析中文地址为 省/市/区(县)/详细 */
export function parseChineseAddress(full: string): AddressParts {
  const result = { province: '', city: '', district: '', detail: '' };
  if (!full) return result;

  let rest = full.trim();

  const provMatch = rest.match(/^(.+?(?:省|自治区|特别行政区))/);
  if (provMatch) {
    result.province = provMatch[1];
    rest = rest.slice(provMatch[1].length);
  } else {
    const directCity = rest.match(/^(.+?市)/);
    if (directCity && /北京|上海|天津|重庆/.test(directCity[1])) {
      result.province = directCity[1];
      rest = rest.slice(directCity[1].length);
    }
  }

  const cityMatch = rest.match(/^(.+?(?:市|自治州|地区|盟))/);
  if (cityMatch) {
    result.city = cityMatch[1];
    rest = rest.slice(cityMatch[1].length);
  }

  const distMatch = rest.match(/^(.+?(?:区|县|市|旗))/);
  if (distMatch) {
    result.district = distMatch[1];
    rest = rest.slice(distMatch[1].length);
  }

  result.detail = rest.trim();
  return result;
}

/** 从原始值（字符串/数组/对象）提取地址各段 */
function extractAddressParts(value: unknown): AddressParts {
  if (value === null || value === undefined || value === '') {
    return { province: '', city: '', district: '', detail: '' };
  }

  if (Array.isArray(value)) {
    const parts = value.map((v) => extractCellValue(v)).filter(Boolean);
    return {
      province: parts[0] ?? '',
      city: parts[1] ?? '',
      district: parts[2] ?? '',
      detail: parts.slice(3).join(''),
    };
  }

  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    if (obj.province || obj.city || obj.district) {
      return {
        province: String(obj.province ?? ''),
        city: String(obj.city ?? ''),
        district: String(obj.district ?? ''),
        detail: String(obj.detail ?? obj.address ?? ''),
      };
    }
  }

  const text = extractCellValue(value);
  return parseChineseAddress(text);
}

/** 归一化列名 */
function normalizeFieldKey(name: string): string {
  return name.replace(/\s/g, '').replace(/[?？]/g, '');
}

/** 合并地址字段：整段优先，否则拼接分项列 */
function buildAddressField(
  fields: Record<string, unknown>,
  options: {
    priorityNames: string[];
    mapKey?: keyof typeof FIELD_MAP;
    partFields?: string[];
    fuzzyIncludes?: string[];
  },
): string {
  const tryFormat = (raw: unknown): string => {
    if (raw === undefined || raw === null) return '';
    const formatted = formatLocationValue(raw);
    if (formatted) return formatted;
    return extractCellValue(raw);
  };

  for (const name of options.priorityNames) {
    const text = tryFormat(fields[name]);
    if (text) return text;
  }

  for (const [fieldName, fieldValue] of Object.entries(fields)) {
    if (options.priorityNames.some((n) => normalizeFieldKey(fieldName) === normalizeFieldKey(n))) {
      const text = tryFormat(fieldValue);
      if (text) return text;
    }
  }

  if (options.mapKey) {
    const resolved = resolveFieldValue(fields, options.mapKey);
    if (resolved) {
      const text = formatLocationValue(resolved) || resolved;
      if (text) return text;
    }
  }

  if (options.partFields?.length) {
    const parts = options.partFields
      .map((name) => extractCellValue(fields[name]))
      .filter(Boolean);
    if (parts.length) return parts.join('');
  }

  for (const keyword of options.fuzzyIncludes ?? []) {
    for (const [fieldName, fieldValue] of Object.entries(fields)) {
      if (fieldName.includes(keyword)) {
        const text = tryFormat(fieldValue);
        if (text) return text;
      }
    }
  }

  return '';
}

function buildHukouAddress(fields: Record<string, unknown>): string {
  for (const name of ['户籍地址', '户口地址', '户口所在地']) {
    const raw = fields[name];
    if (raw !== undefined && raw !== null && raw !== '') {
      const text = formatLocationValue(raw) || extractCellValue(raw);
      if (text) return text;
    }
  }
  return buildAddressField(fields, {
    priorityNames: ['户籍地址', '户口地址', '户口所在地'],
    mapKey: 'hukouAddress',
    partFields: ['户籍省', '户籍市', '户籍区', '户口省', '户口市', '户口区'],
    fuzzyIncludes: ['户籍地址', '户口地址'],
  });
}

function buildCurrentAddress(fields: Record<string, unknown>): string {
  for (const name of ['现居住址', '现居地址']) {
    const raw = fields[name];
    if (raw !== undefined && raw !== null && raw !== '') {
      const text = formatLocationValue(raw) || extractCellValue(raw);
      if (text) return text;
    }
  }
  return buildAddressField(fields, {
    priorityNames: ['现居住址', '现居地址'],
    mapKey: 'currentAddress',
    partFields: ['现居省', '现居市', '现居区', '现居路', '现居详细地址'],
    fuzzyIncludes: ['现居住址', '现居地址'],
  });
}

/** 按字段类型做最终格式化 */
function formatFieldValue(key: keyof typeof FIELD_MAP, raw: string): string {
  if (!raw) return '';

  if (DATE_FIELDS.has(key)) return formatDate(raw);
  if (key === 'email') return formatEmail(raw);
  if (key === 'expectedSalary') return formatSalary(raw);
  if (UNIT_FIELDS[key]) return formatWithUnit(raw, UNIT_FIELDS[key]!);

  return raw;
}

const COLUMN_NAME_ALIASES: Record<string, string[]> = {
  '任职时间（起）': ['任职时间 (起)', '任职时间(起)'],
  '任职时间（止）': ['任职时间 (止)', '任职时间(止)'],
  '学习时间（起）': ['学习时间 (起)', '学习时间(起)'],
  '学习时间（止）': ['学习时间 (止)', '学习时间(止)'],
};

function normalizeColumnKey(name: string): string {
  return name.replace(/\s/g, '').replace(/[（(]/g, '(').replace(/[）)]/g, ')');
}

/** 按列名读取单元格，兼容全角/半角括号与空格差异 */
function resolveColumnText(fields: Record<string, unknown>, fieldName?: string): string {
  if (!fieldName) return '';
  const candidates = [fieldName, ...(COLUMN_NAME_ALIASES[fieldName] ?? [])];
  for (const candidate of candidates) {
    const value = extractCellValue(fields[candidate]);
    if (value) return value;
  }
  const target = normalizeColumnKey(fieldName);
  for (const [key, value] of Object.entries(fields)) {
    if (normalizeColumnKey(key) === target) {
      const resolved = extractCellValue(value);
      if (resolved) return resolved;
    }
  }
  return '';
}

/** 合并起止时间列为区间字符串 */
function buildPeriodFromRange(
  fields: Record<string, unknown>,
  startKey?: string,
  endKey?: string,
): string {
  if (!startKey && !endKey) return '';
  const start = startKey ? resolveColumnText(fields, startKey) : '';
  const end = endKey ? resolveColumnText(fields, endKey) : '';
  if (start && end) return formatDateRange(`${start}~${end}`);
  if (start) return formatDateRange(start);
  if (end) return formatDateRange(end);
  return '';
}

const MIN_SUB_TABLE_ROWS = 3;

type SubRow = Record<string, string>;

function rowHasData(row: SubRow): boolean {
  return Object.values(row).some((value) => value.trim() !== '');
}

function dedupeSubRows(rows: SubRow[]): SubRow[] {
  const seen = new Set<string>();
  const result: SubRow[] = [];
  for (const row of rows) {
    if (!rowHasData(row)) continue;
    const key = Object.values(row).map((value) => value.trim()).join('\u0001');
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(row);
  }
  return result;
}

function extractFamilyRowsFromFields(fields: Record<string, unknown>): SubRow[] {
  const rows: SubRow[] = [];
  FAMILY_FIELDS.forEach((rowDef) => {
    const row: SubRow = {};
    let hasData = false;
    Object.entries(rowDef).forEach(([col, fieldName]) => {
      if (!fieldName || col === 'periodStart' || col === 'periodEnd') return;
      const value = resolveColumnText(fields, fieldName);
      row[col] = value;
      if (value) hasData = true;
    });
    if (hasData) rows.push(row);
  });
  return rows;
}

function extractWorkRowsFromFields(fields: Record<string, unknown>): SubRow[] {
  const rows: SubRow[] = [];
  WORK_FIELDS.forEach((rowDef) => {
    const typed = rowDef as Record<string, string | undefined>;
    const row: SubRow = {};
    let hasData = false;
    if (typed.periodStart || typed.periodEnd) {
      const period = buildPeriodFromRange(fields, typed.periodStart, typed.periodEnd);
      row.period = period;
      if (period) hasData = true;
    }
    Object.entries(rowDef).forEach(([col, fieldName]) => {
      if (!fieldName || col === 'periodStart' || col === 'periodEnd') return;
      const raw = resolveColumnText(fields, fieldName);
      const value = PERIOD_FIELD_KEYS.includes(col) ? formatDateRange(raw) : raw;
      row[col] = value;
      if (value) hasData = true;
    });
    if (hasData) rows.push(row);
  });
  return rows;
}

function extractEducationRowsFromFields(fields: Record<string, unknown>): SubRow[] {
  const rows: SubRow[] = [];
  EDUCATION_FIELDS.forEach((rowDef) => {
    const typed = rowDef as Record<string, string | undefined>;
    const row: SubRow = {};
    let hasData = false;
    if (typed.periodStart || typed.periodEnd) {
      const period = buildPeriodFromRange(fields, typed.periodStart, typed.periodEnd);
      row.period = period;
      if (period) hasData = true;
    }
    Object.entries(rowDef).forEach(([col, fieldName]) => {
      if (!fieldName || col === 'periodStart' || col === 'periodEnd') return;
      const raw = resolveColumnText(fields, fieldName);
      const value = PERIOD_FIELD_KEYS.includes(col) ? formatDateRange(raw) : raw;
      row[col] = value;
      if (value) hasData = true;
    });
    if (hasData) rows.push(row);
  });
  return rows;
}

function applyFamilyRowsToForm(form: FormData, rows: SubRow[]) {
  const displayRows = rows.length ? rows : [];
  const count = Math.max(MIN_SUB_TABLE_ROWS, displayRows.length);
  for (let i = 0; i < count; i += 1) {
    const row = displayRows[i] ?? {};
    (['name', 'relation', 'age', 'employer', 'position', 'address'] as const).forEach((col) => {
      form[`family_${i}_${col}`] = row[col] ?? '';
    });
  }
}

function applyWorkRowsToForm(form: FormData, rows: SubRow[]) {
  const displayRows = rows.length ? rows : [];
  const count = Math.max(MIN_SUB_TABLE_ROWS, displayRows.length);
  for (let i = 0; i < count; i += 1) {
    const row = displayRows[i] ?? {};
    (['period', 'company', 'position', 'salary', 'reason', 'reference'] as const).forEach((col) => {
      form[`work_${i}_${col}`] = row[col] ?? '';
    });
  }
}

function applyEducationRowsToForm(form: FormData, rows: SubRow[]) {
  const displayRows = rows.length ? rows : [];
  const count = Math.max(MIN_SUB_TABLE_ROWS, displayRows.length);
  for (let i = 0; i < count; i += 1) {
    const row = displayRows[i] ?? {};
    (['period', 'school', 'major', 'degree'] as const).forEach((col) => {
      form[`edu_${i}_${col}`] = row[col] ?? '';
    });
  }
}

function countBasicFieldsFilled(fields: Record<string, unknown>): number {
  let count = 0;
  (Object.keys(FIELD_MAP) as Array<keyof typeof FIELD_MAP>).forEach((key) => {
    if (resolveFieldValue(fields, key)) count += 1;
  });
  if (buildHukouAddress(fields)) count += 1;
  if (buildCurrentAddress(fields)) count += 1;
  return count;
}

/** 从多条同名记录中选取基本信息最完整的一条（作兜底） */
function pickRichestProfileFields(
  records: Record<string, unknown>[],
): Record<string, unknown> {
  if (!records.length) return {};
  return records.reduce((best, current) => (
    countBasicFieldsFilled(current) > countBasicFieldsFilled(best) ? current : best
  ));
}

/** 从同名记录中补全仍为空的基本信息字段（如亲友任职，可能只在主记录行有值） */
function fillMissingProfileFields(
  profileFields: Record<string, unknown>,
  allRecords: Record<string, unknown>[],
) {
  (Object.keys(FIELD_MAP) as Array<keyof typeof FIELD_MAP>).forEach((key) => {
    if (resolveFieldValue(profileFields, key)) return;
    for (const fields of allRecords) {
      const namesToTry = [FIELD_MAP[key], ...(FIELD_ALIASES[key] ?? [])];
      for (const fieldName of namesToTry) {
        const raw = fields[fieldName];
        if (raw !== undefined && raw !== null && raw !== '' && extractCellValue(raw)) {
          profileFields[fieldName] = raw;
          return;
        }
      }
      const keywords = FIELD_KEYWORDS[key] ?? [];
      if (keywords.length > 1) {
        for (const [fieldName, fieldValue] of Object.entries(fields)) {
          if (keywords.every((keyword) => fieldName.includes(keyword))
            && fieldValue !== undefined && fieldValue !== null && fieldValue !== ''
            && extractCellValue(fieldValue)) {
            profileFields[fieldName] = fieldValue;
            return;
          }
        }
      }
    }
  });
}

/** 合并基本信息：优先当前选中行，空缺时从最完整的其他行补全 */
function mergeProfileFields(
  primaryFields: Record<string, unknown>,
  relatedFieldsList: Record<string, unknown>[],
): Record<string, unknown> {
  const merged = {
    ...pickRichestProfileFields([primaryFields, ...relatedFieldsList]),
  };
  for (const [fieldName, fieldValue] of Object.entries(primaryFields)) {
    if (fieldValue !== undefined && fieldValue !== null && fieldValue !== ''
      && extractCellValue(fieldValue)) {
      merged[fieldName] = fieldValue;
    }
  }
  return merged;
}

function countFilledSubRows(form: FormData, prefix: 'family' | 'work' | 'edu', cols: string[]): number {
  return getSubTableRowIndices(form, prefix, 0).filter((index) => (
    cols.some((col) => (form[`${prefix}_${index}_${col}`] ?? '').trim() !== '')
  )).length;
}

/** 统计子表已填充行数 */
export function getSubTableStats(form: FormData) {
  return {
    family: countFilledSubRows(form, 'family', ['name', 'relation', 'employer', 'address']),
    work: countFilledSubRows(form, 'work', ['company', 'period', 'position']),
    edu: countFilledSubRows(form, 'edu', ['school', 'major', 'degree']),
  };
}

/** 子表行数（至少 3 行，有数据时自动扩展） */
export function getSubTableRowIndices(
  form: FormData,
  prefix: 'family' | 'work' | 'edu',
  minRows = MIN_SUB_TABLE_ROWS,
): number[] {
  let maxIdx = -1;
  Object.keys(form).forEach((key) => {
    const matched = key.match(new RegExp(`^${prefix}_(\\d+)_`));
    if (matched) maxIdx = Math.max(maxIdx, parseInt(matched[1], 10));
  });
  const count = Math.max(minRows, maxIdx + 1);
  return Array.from({ length: count }, (_, index) => index);
}

/** 将 AI 表格记录字段映射为表单数据 */
export function mapRecordToForm(fields: Record<string, unknown>): FormData {
  const form: FormData = {};

  (Object.keys(FIELD_MAP) as Array<keyof typeof FIELD_MAP>).forEach((key) => {
    const raw = resolveFieldValue(fields, key);
    form[key] = formatFieldValue(key, raw);
  });

  form.hukouAddress = buildHukouAddress(fields);
  form.currentAddress = buildCurrentAddress(fields);

  applyFamilyRowsToForm(form, extractFamilyRowsFromFields(fields));
  applyWorkRowsToForm(form, extractWorkRowsFromFields(fields));
  applyEducationRowsToForm(form, extractEducationRowsFromFields(fields));

  DEVELOPMENT_PLAN_ROWS.forEach((row) => {
    form[`plan_${row.key}_life`] = resolveDevelopmentPlanField(fields, row, 'life');
    form[`plan_${row.key}_career`] = resolveDevelopmentPlanField(fields, row, 'career');
  });

  return form;
}

/**
 * 合并主记录 + 同名关联记录，汇总家庭/工作/教育多条子记录
 */
export function mapRecordsToForm(
  primaryFields: Record<string, unknown>,
  relatedFieldsList: Record<string, unknown>[] = [],
): FormData {
  const allFields = [primaryFields, ...relatedFieldsList];
  // 基本信息：优先选中行，仅补全空缺（避免工作经历行覆盖出生日期等）
  const profileFields = mergeProfileFields(primaryFields, relatedFieldsList);
  fillMissingProfileFields(profileFields, allFields);
  const form = mapRecordToForm(profileFields);
  fillMissingDevelopmentPlanFields(form, allFields);

  const familyRows: SubRow[] = [];
  const workRows: SubRow[] = [];
  const eduRows: SubRow[] = [];

  // 每条同名记录各贡献一段家庭/工作/教育（一行记录 ≈ 一段经历）
  allFields.forEach((fields) => {
    familyRows.push(...extractFamilyRowsFromFields(fields));
    workRows.push(...extractWorkRowsFromFields(fields));
    eduRows.push(...extractEducationRowsFromFields(fields));
  });

  applyFamilyRowsToForm(form, dedupeSubRows(familyRows));
  applyWorkRowsToForm(form, dedupeSubRows(workRows));
  applyEducationRowsToForm(form, dedupeSubRows(eduRows));

  return form;
}
